import type { VehicleCatalogEntryStatus, VehicleCatalogSource } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { tokens } from '../../../design';

/** Small read-only chips of «Справочник авто» (AUT-437): status, flags, source. */
export const CatalogStatusChip = ({
  status,
}: {
  status: VehicleCatalogEntryStatus;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Chip
      size="small"
      color={status === 'active' ? 'success' : 'default'}
      label={t(`vehicleCatalog.status.${status}`)}
    />
  );
};

export const CatalogFlagChips = ({
  popular,
  onSale,
}: {
  popular: boolean;
  onSale: boolean;
}): ReactElement => {
  const { t } = useTranslation();
  if (!popular && !onSale) {
    return (
      <Typography variant="body2" sx={{ color: tokens.text.tertiary }}>
        {t('vehicleCatalog.noValue')}
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {popular ? (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label={t('vehicleCatalog.flags.popular')}
        />
      ) : null}
      {onSale ? (
        <Chip
          size="small"
          color="success"
          variant="outlined"
          label={t('vehicleCatalog.flags.onSale')}
        />
      ) : null}
    </Box>
  );
};

export const CatalogSourceLabel = ({ source }: { source: VehicleCatalogSource }): ReactElement => {
  const { t } = useTranslation();
  return <Typography variant="body2">{t(`vehicleCatalog.source.${source}`)}</Typography>;
};

/** First letters of the name — the mockup's grey square. */
export const MakeInitials = ({
  name,
  size = 32,
}: {
  name: string;
  size?: number;
}): ReactElement => {
  const initials = name
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'action.hover',
        fontWeight: 700,
        fontSize: size * 0.4,
      }}
    >
      {initials}
    </Box>
  );
};
